import React from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Flex,
  Tooltip,
  Spinner,
  Center,
} from '@chakra-ui/react';

const ProjectOverview = ({ data, loading }) => {
  // ✅ Extract from parent dashboard data
  const projects =
    data?.projectOverview?.map((p) => ({
      name: p.project_name,
      todo: Number(p.todo_percentage),
      inProgress: Number(p.in_progress_percentage),
      done: Number(p.done_percentage),
      review: Number(p.review_percentage),
    })) ?? [];

  const maxValue = Math.max(
    ...projects.map((p) => p.todo + p.inProgress + p.done + p.review),
    1, // prevents division by 0
  );

  const axisLabels = [0, 50, 100, 150, 200];

  return (
    <>
      <Text fontSize="md" fontWeight="semibold" mb={1}>
        Project Overview
      </Text>

      <Box
        bg="white"
        borderRadius="lg"
        p={4}
        boxShadow="sm"
        borderWidth="1px"
        borderColor="gray.200"
        position="relative"
         h="310px"
      >
        <Text fontSize="xs" color="gray.400" mb={4}>
          Tasks distribution across projects
        </Text>

        {loading ? (
          <Center h="150px">
            <Spinner />
          </Center>
        ) : (
          <>
            {/* Axis Labels */}
            <HStack justify="space-between" ml="60px" mb={2} px={1}>
              {axisLabels.map((label) => (
                <Text key={label} fontSize="xs" color="gray.400">
                  {label}
                </Text>
              ))}
            </HStack>

            {/* Chart Bars */}
            <VStack
              spacing={4}
              align="stretch"
              maxH="120px"
              overflowY="auto"
              pr={1}
            >
              {projects.map((project, index) => {
                const total =
                  project.todo +
                  project.inProgress +
                  project.done +
                  project.review;
                const todoPercent = (project.todo / maxValue) * 100;
                const progressPercent = (project.inProgress / maxValue) * 100;
                const donePercent = (project.done / maxValue) * 100;
                const reviewPercent = (project.review / maxValue) * 100;

                return (
                  <VStack key={index} spacing={1} align="stretch">
                    <Flex align="center" gap={3}>
                      <Text
                        mb="0"
                        fontSize="sm"
                        fontWeight="600"
                        color="gray.700"
                        w="55px"
                        flexShrink={0}
                      >
                        {project.name}
                      </Text>

                      <Flex
                        w="full"
                        h="20px"
                        overflow="hidden"
                        position="relative"
                      >
                        <Tooltip
                          label={
                            <Box pt={0}>
                              <Text
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.700"
                                mb={1}
                              >
                                {project.name}
                              </Text>
                              <HStack spacing={2}>
                                <Box
                                  w="10px"
                                  h="10px"
                                  borderRadius="sm"
                                  bg="purple.500"
                                />
                                <Text fontSize="xs" color="gray.500" mb={0}>
                                  To Do
                                </Text>
                                <Text
                                  fontSize="xs"
                                  fontWeight="700"
                                  color="purple.500"
                                  ml="auto"
                                  mb={0}
                                >
                                  {project.todo}
                                </Text>
                              </HStack>
                              <HStack spacing={2} mt={1}>
                                <Box
                                  w="10px"
                                  h="10px"
                                  borderRadius="sm"
                                  bg="red.400"
                                />
                                <Text fontSize="xs" color="gray.500" mb={0}>
                                  In Progress
                                </Text>
                                <Text
                                  fontSize="xs"
                                  fontWeight="700"
                                  color="red.400"
                                  ml="auto"
                                  mb={0}
                                >
                                  {project.inProgress}
                                </Text>
                              </HStack>
                              <HStack spacing={2} mt={1}>
                                <Box
                                  w="10px"
                                  h="10px"
                                  borderRadius="sm"
                                  bg="blue.400"
                                />
                                <Text fontSize="xs" color="gray.500" mb={0}>
                                  Review
                                </Text>
                                <Text
                                  fontSize="xs"
                                  fontWeight="700"
                                  color="blue.400"
                                  ml="auto"
                                  mb={0}
                                >
                                  {project.review}
                                </Text>
                              </HStack>
                              <HStack spacing={2} mt={1}>
                                <Box
                                  w="10px"
                                  h="10px"
                                  borderRadius="sm"
                                  bg="cyan.400"
                                />
                                <Text fontSize="xs" color="gray.500" mb={0}>
                                  Done
                                </Text>
                                <Text
                                  fontSize="xs"
                                  fontWeight="700"
                                  color="cyan.400"
                                  ml="auto"
                                  mb={0}
                                >
                                  {project.done}
                                </Text>
                              </HStack>
                              <Flex
                                borderTopWidth="1px"
                                borderColor="gray.200"
                                mt={2}
                                pt={1}
                                justify="space-between"
                              >
                                <Text fontSize="xs" color="gray.400">
                                  Total
                                </Text>
                                <Text
                                  fontSize="xs"
                                  fontWeight="700"
                                  color="gray.700"
                                >
                                  {total.toFixed(2)}
                                </Text>
                              </Flex>
                            </Box>
                          }
                          bg="white"
                          color="gray.800"
                          boxShadow="lg"
                          borderRadius="lg"
                          p={3}
                          borderWidth="1px"
                          borderColor="gray.200"
                          hasArrow
                          placement="top"
                        >
                          <Flex
                            w="full"
                            h="full"
                            overflow="hidden"
                            cursor="pointer"
                          >
                            <Box
                              w={`${todoPercent}%`}
                              h="full"
                              bg="purple.500"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              transition="opacity 0.2s"
                              _hover={{ opacity: 0.85 }}
                            >
                              <Text
                                mb={0}
                                fontSize="10px"
                                fontWeight="700"
                                color="white"
                              >
                                {todoPercent > 10 ? project.todo : ''}
                              </Text>
                            </Box>

                            <Box
                              w={`${progressPercent}%`}
                              h="full"
                              bg="red.400"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              transition="opacity 0.2s"
                              _hover={{ opacity: 0.85 }}
                            >
                              <Text
                                mb={0}
                                fontSize="10px"
                                fontWeight="700"
                                color="white"
                              >
                                {progressPercent > 10 ? project.inProgress : ''}
                              </Text>
                            </Box>

                            <Box
                              w={`${reviewPercent}%`}
                              h="full"
                              bg="green.400"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              transition="opacity 0.2s"
                              _hover={{ opacity: 0.85 }}
                            >
                              <Text
                                mb={0}
                                fontSize="10px"
                                fontWeight="700"
                                color="white"
                              >
                                {reviewPercent > 10 ? project.review : ''}
                              </Text>
                            </Box>

                            <Box
                              w={`${donePercent}%`}
                              h="full"
                              bg="cyan.400"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              transition="opacity 0.2s"
                              _hover={{ opacity: 0.85 }}
                            >
                              <Text
                                mb={0}
                                fontSize="10px"
                                fontWeight="700"
                                color="white"
                              >
                                {donePercent > 10 ? project.done : ''}
                              </Text>
                            </Box>

                          </Flex>
                        </Tooltip>
                      </Flex>
                    </Flex>
                  </VStack>
                );
              })}
            </VStack>
          </>
        )}

        {/* Legend */}
        <HStack spacing={6} mt={5} justify="center">
          <HStack spacing={2}>
            <Box w="12px" h="12px" borderRadius="sm" bg="purple.500" />
            <Text mb="0" fontSize="xs" color="gray.500" fontWeight="500">
              To Do
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Box w="12px" h="12px" borderRadius="sm" bg="red.400" />
            <Text mb="0" fontSize="xs" color="gray.500" fontWeight="500">
              In Progress
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Box w="12px" h="12px" borderRadius="sm" bg="green.400" />
            <Text mb="0" fontSize="xs" color="gray.500" fontWeight="500">
              Review
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Box w="12px" h="12px" borderRadius="sm" bg="cyan.400" />
            <Text mb="0" fontSize="xs" color="gray.500" fontWeight="500">
              Done
            </Text>
          </HStack>
        </HStack>
      </Box>
    </>
  );
};

export default ProjectOverview;
 